import { useState } from "react";
import { Spinner } from "react-bootstrap";
import { CheckAll, CloudUploadFill, XLg } from "react-bootstrap-icons";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import { useError } from "../../contexts/ErrorContext";
import { useSuccess } from "../../contexts/SuccessContext";
import { VideosAPI } from "../../services/api";
import { Execution, VideoRecord } from "../../types";
import { BatchUploadModal } from "../batch/BatchUploadModal";
import UploadModal from "../UploadModal";
import BatchWorkflowModal from "../workflow/BatchWorkflowModal";
import DownloadForSelectedRecords from "./DownloadForSelectedRecords";
import RecordItem from "./RecordItem";

interface RecordListProps {
  loadingRecords: boolean;
  records: VideoRecord[];
  onRecordUpdated: (record: VideoRecord | null) => void;
  onExecutionUpdated: (recordId: number, execution: Execution) => void;
}

const RecordList: React.FC<RecordListProps> = ({
  loadingRecords,
  records,
  onRecordUpdated,
  onExecutionUpdated,
}) => {
  const [selectedRecord, setSelectedRecord] = useState<VideoRecord | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showBatchWorkflowModal, setShowBatchWorkflowModal] = useState(false);

  // Initialize with all records checked by default
  const [checkedRecords, setCheckedRecords] = useState<Set<number>>(
    new Set(records.map(record => record.id))
  );
  const [prevRecords, setPrevRecords] = useState<VideoRecord[]>(records);
  const { setError } = useError();
  const { setSuccess } = useSuccess();
  const [downloadVersions, setDownloadVersions] = useState<{ [recordId: number]: number }>({});


  const handleSetMasterVideo = async (record: VideoRecord, videoId: number) => {
    try {
      await VideosAPI.setMaster(videoId);
      onRecordUpdated(record);
      // Remount the record's download components so they refetch their file lists
      setDownloadVersions(prev => ({ ...prev, [record.id]: (prev[record.id] ?? 0) + 1 }));
    } catch (error) {
      setError(`Failed to set master video ${error}`);
    }
  };

  const handleUploadComplete = (result?: { success: boolean; message: string }) => {
    if (result) {
      // Show success or error message
      if (result.success) {
        setSuccess(result.message);
        onRecordUpdated(selectedRecord);

      } else {
        setError(result.message);
      }
    } else {
      // Backward compatibility for old behavior
      setShowUploadModal(false);
      onRecordUpdated(selectedRecord);
    }
  };

  const handleBatchUploadComplete = (result?: { success: boolean; message: string }) => {
    setShowBatchModal(false);

    if (result) {
      if (result.success) {
        setSuccess(result.message);
        onRecordUpdated(null);
      } else {
        setError(result.message);
      }
    } else {
      onRecordUpdated(null);
    }
  };


  const checkAtLeastOneRecordChecked = () => {
    const selectedRecords = getCheckedRecords();
    if (selectedRecords.length === 0) {
      setError("Please select at least one record");
      return false;
    }
    return true;
  };

  const handleStartBatchWorkflow = () => {
    if (!checkAtLeastOneRecordChecked()) return;
    setShowBatchWorkflowModal(true);
  };


  const handleBatchWorkflowComplete = (records: VideoRecord[]) => {
    setShowBatchWorkflowModal(false);
    // Refresh all records to show updated execution status
    records.forEach(record => {
      onRecordUpdated(record);
    });
  };


  const handleUploadVideo = (record: VideoRecord) => {
    setSelectedRecord(record);
    setShowUploadModal(true);
  };

  const handleCheckChange = (recordId: number, checked: boolean) => {
    setCheckedRecords(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(recordId);
      } else {
        newSet.delete(recordId);
      }
      return newSet;
    });
  };

  const getCheckedRecords = (): VideoRecord[] => {
    return records.filter(record => checkedRecords.has(record.id));
  };


  // Update checked records when the records list changes (auto-check newly added records).
  // Adjusted during render rather than in an effect, so new records are already checked
  // the first time they are displayed.
  if (records !== prevRecords) {
    const prevIds = new Set(prevRecords.map(record => record.id));
    setPrevRecords(records);
    setCheckedRecords(prev => {
      const updated = new Set(prev);
      records.forEach(record => {
        if (!prevIds.has(record.id)) {
          updated.add(record.id);
        }
      });
      return updated;
    });
  }

  return (
    <Container className="records-container p-0">
      <div className="d-flex mb-3 gap-2 flex-wrap">
        <div className="d-flex me-auto">

          <Button
            variant="primary"
            onClick={() => setShowBatchModal(true)}
            className="d-flex align-items-center gap-2 me-2"
          >
            <CloudUploadFill size={16} />
            Batch video upload
          </Button>

          {/* Batch workflow button */}
          <div className="d-flex align-items-center gap-2">
            <Button onClick={() => handleStartBatchWorkflow()}>
              Batch Workflows
            </Button>
          </div>
        </div>

        {records.length > 0 && (
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                const allIds = new Set(records.map(record => record.id));
                setCheckedRecords(allIds);
              }}
              className="d-flex align-items-center justify-content-center"
              style={{ width: '30px', height: '30px', padding: '0' }}
              title="Check All"
            >
              <CheckAll size={16} />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setCheckedRecords(new Set())}
              className="d-flex align-items-center justify-content-center"
              style={{ width: '30px', height: '30px', padding: '0' }}
              title="Uncheck All"
            >
              <XLg size={16} />
            </Button>
          </div>
        )}
      </div>
      {loadingRecords ? (
        <Spinner animation="border" size="sm" />
      ) : (<>
        {/* Download buttons for all selected records */}
        {getCheckedRecords().length > 0 && (
          <DownloadForSelectedRecords records={getCheckedRecords()} />
        )}

        {records.map((record) => (
          <RecordItem
            key={record.id + "_" + String(record.iteration)}
            record={record}
            onSetMasterVideo={handleSetMasterVideo}
            onUploadVideo={handleUploadVideo}
            recordDownloadKey={String(downloadVersions[record.id] ?? 0)}
            isChecked={checkedRecords.has(record.id)}
            onCheckChange={handleCheckChange}
            onExecutionUpdated={onExecutionUpdated}
          />
        ))}

      </>)}
      {/* Modals */}

      {showBatchModal && <BatchUploadModal
        onHide={() => setShowBatchModal(false)}
        onComplete={handleBatchUploadComplete}
      />}
      <BatchWorkflowModal
        show={showBatchWorkflowModal}
        onHide={() => setShowBatchWorkflowModal(false)}
        onComplete={handleBatchWorkflowComplete}
        records={getCheckedRecords()}
      />

      {selectedRecord && (
        <>
          <UploadModal
            show={showUploadModal}
            onHide={() => setShowUploadModal(false)}
            onComplete={handleUploadComplete}
            recordId={selectedRecord.id}
            recordType={selectedRecord.type}
          />
        </>
      )}
    </Container>
  );
};

export default RecordList;
